import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { ReportsApi } from '../../Services/ConciliationApiDataService';
import ServerTable from '../../Components/Table/ServerTabe';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';
import { Permissions } from '../../Security/permissions';
import { useSecurity } from '../../Contexts/SecurityContext';

/* small util */
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
};

const useToken = () => {
  const { instance, accounts } = useMsal();
  return React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [instance, accounts]);
};

const MissingInfoReportContent = () => {
  const getToken = useToken();
  const { companiesFor, hasCompanyPermission } = useSecurity();

  // company picker
  const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);

  // filters / toolbar
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [search, setSearch] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [vendorCode, setVendorCode] = React.useState('');
  const [nit, setNit] = React.useState('');
  const [minAmount, setMinAmount] = React.useState('');
  const [maxAmount, setMaxAmount] = React.useState('');
  const [workType, setWorkType] = React.useState(null); // null | 'VendorPayment' | 'PurchaseInvoice'

  const workTypeOptions = [
    { value: null, label: 'All types' },
    { value: 'VendorPayment', label: 'Vendor Payment (from invoice)' },
    { value: 'PurchaseInvoice', label: 'Purchase Invoice (from bank txn)' }
  ];

  // preload companies (same as other screens)
  React.useEffect(() => {
    const opts = companiesFor(Permissions.Conciliation.Reports).map(company => ({
      value: company.companyId,
      label: company.companyName || company.companyDb || `Company ${company.companyId}`
    }));
    setCompanyOptions(opts);
    setLoadingCompanies(false);
    if (companyId && !opts.some(option => option.value === companyId)) {
      setCompanyId(null);
      setStoredConciliationCompanyId(null);
    }
  }, [companiesFor, companyId]);

  const authorizedCompanyId = companyId && hasCompanyPermission(companyId, Permissions.Conciliation.Reports)
    ? companyId
    : null;

  // server-side fetch
  const fetchData = React.useCallback(async ({ pageIndex, pageSize }) => {
    if (!authorizedCompanyId) return { items: [], totalItems: 0 };
    const token = await getToken();

    const res = await ReportsApi.getMissingInfo(
      authorizedCompanyId,
      {
        page: pageIndex + 1,
        pageSize,
        search: (search || '').trim(),
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        vendorCode: vendorCode || null,
        nit: nit || null,
        minAmount: minAmount ? Number(minAmount) : null,
        maxAmount: maxAmount ? Number(maxAmount) : null,
        workType: workType?.value ?? null
      },
      token
    );

    return {
      items: res.items ?? [],
      totalItems: res.totalItems ?? 0
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizedCompanyId, getToken, refreshTick, search, dateFrom, dateTo, vendorCode, nit, minAmount, maxAmount, workType]);

  // export
  const exportFile = async (format) => {
    if (!authorizedCompanyId) return;
    try {
      const token = await getToken();
      const blob = await ReportsApi.exportMissingInfo(
        authorizedCompanyId,
        {
          search: (search || '').trim(),
          dateFrom: dateFrom || null,
          dateTo: dateTo || null,
          vendorCode: vendorCode || null,
          nit: nit || null,
          minAmount: minAmount ? Number(minAmount) : null,
          maxAmount: maxAmount ? Number(maxAmount) : null,
          workType: workType?.value ?? null
        },
        format,
        token
      );
      const filename = `missing_info_${authorizedCompanyId}_${new Date().toISOString().slice(0,10)}.${format}`;
      saveBlob(blob, filename);
    } catch (e) {
      console.error('Export failed', e);
      alert('Export failed. Please try again.');
    }
  };

  // columns — align them with your MissingInfoRowDto (adjust names if needed)
  // Suggested shape (from earlier planning):
  // workType, entityId, candidateAction, sourceInvoiceId, bankTxnId,
  // vendorCode, vendorName, vendorNit, cardLast4,
  // docDate/valueDate, currency, amount,
  // requiredFieldsMissing (string[] or csv), suggestions (gl/costCenter/vendor),
  // firstSeenOn, lastCheckedOn
  const columns = React.useMemo(() => [
    { header: 'Type', accessorKey: 'workType' },                      // 'VendorPayment' | 'PurchaseInvoice'
    { header: 'Work Id', accessorKey: 'entityId' },                   // Id in Work_* table
    { header: 'Action', accessorKey: 'candidateAction' },             // 'VendorPayment' | 'PurchaseInvoice'
    { header: 'SourceInvoiceId', accessorKey: 'sourceInvoiceId' },    // if applicable
    { header: 'BankTxnId', accessorKey: 'bankTxnId' },                // if applicable
    { header: 'Vendor Code', accessorKey: 'vendorCode' },
    { header: 'Vendor Name', accessorKey: 'vendorName' },
    { header: 'NIT', accessorKey: 'vendorNit' },
    { header: 'Card Last4', accessorKey: 'cardLast4' },
    {
      header: 'Doc/Value Date',
      accessorKey: 'documentDate',
        cell: info => formatGuatemalanDateTime(info.getValue()),
        meta: {
            filterVariant: 'date-range',
        },
        filterFn: 'dateBetweenFilterFn' 
    },
    { header: 'Currency', accessorKey: 'currency' },
    {
      header: 'Amount',
      accessorKey: 'amount',
      cell: info => info.getValue() != null ? Number(info.getValue()).toLocaleString() : '',
      meta: { filterVariant: 'range' }
    },
    {
      header: 'Missing Fields',
      accessorKey: 'requiredFieldsMissing',
      cell: info => {
        const v = info.getValue();
        if (!v) return '';
        if (Array.isArray(v)) return v.join(', ');
        return String(v);
      }
    },
    { header: 'Suggest GL', accessorKey: 'suggestedGlAccount' },
    { header: 'Suggest CC', accessorKey: 'suggestedCostCenter' },
    { header: 'Suggest Vendor', accessorKey: 'suggestedVendorCode' },
    {
      header: 'First Seen',
      accessorKey: 'firstSeenOn',
      cell: info => formatGuatemalanDateTime(info.getValue()),
              meta: {
                  filterVariant: 'date-range',
              },
              filterFn: 'dateBetweenFilterFn'
    },
    {
      header: 'Last Checked',
      accessorKey: 'lastCheckedOn',
      cell: info => formatGuatemalanDateTime(info.getValue()),
              meta: {
                  filterVariant: 'date-range',
              },
              filterFn: 'dateBetweenFilterFn'
    },
  ], []);

  return (
    <Container fluid className="h-100 d-flex flex-column">
      {/* Toolbar */}
      <div className="d-flex justify-content-between align-items-end w-100 mb-3">
        <div className="d-flex align-items-end" style={{ gap: 12 }}>
          <div style={{ minWidth: 280 }}>
            <label className="form-label mb-1">Company</label>
            <Select
              classNamePrefix="react-select"
              options={[{ value: null, label: 'Select company...' }, ...companyOptions]}
              value={companyOptions.find(o => o.value === companyId) ?? null}
              onChange={(opt) => {
                const id = opt?.value ?? null;
                setCompanyId(id);
                setStoredConciliationCompanyId(id);
              }}
              isLoading={loadingCompanies}
              placeholder="Select company…"
              menuPortalTarget={document.body}
              styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
            />
          </div>
          <div style={{ minWidth: 240 }}>
            <label className="form-label mb-1">Type</label>
            <Select
              classNamePrefix="react-select"
              options={workTypeOptions}
              value={workTypeOptions.find(o => o.value === (workType?.value ?? workType)) ?? workTypeOptions[0]}
              onChange={(opt) => setWorkType(opt)}
              placeholder="All types"
              menuPortalTarget={document.body}
              styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
            />
          </div>
        </div>

        <div className="d-flex align-items-end" style={{ gap: 8 }}>
          <button
            className="btn btn-outline-primary fw-bold"
            onClick={() => setRefreshTick(t => t + 1)}
            disabled={!authorizedCompanyId}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
          <button
            className="btn btn-primary fw-bold"
            onClick={() => exportFile('csv')}
            disabled={!authorizedCompanyId}
            title="Export CSV"
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            CSV
          </button>
          <button
            className="btn btn-primary fw-bold"
            onClick={() => exportFile('xlsx')}
            disabled={!authorizedCompanyId}
            title="Export Excel"
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <Row className="my-2">
        <Col sm={12}>
          {!authorizedCompanyId && (
            <div className="alert alert-info mb-3">
              Select a company to load missing-info records.
            </div>
          )}
          <ServerTable
            columns={columns}
            fetchData={fetchData}
            tableClass="table-hover"
            containerClass="table-responsive"
            headerClass="table-light fit-header"
            enableGlobalFilter={false}
            initialPageSize={20}
          />
        </Col>
      </Row>
    </Container>
  );
};

const MissingInfoReportPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Matchable Items with Missing Info">
      <MissingInfoReportContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default MissingInfoReportPage;
